import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { inviteCompanyMember } from '../../services/api';
import type { OrbitOverviewDossier, OrbitRoomDepartment, OrbitRoomPerson } from '../../types/aai';
import { shortName } from './roomCopy';
import { ContextPicker } from './ContextPicker';
import type { ContextPickerHandle } from './ContextPicker';

/*
 * Phase 2: department context, one department at a time.
 *
 * Orbit drafts every department from what it read, then each one is made true
 * one of two ways: the head answers what the draft could not, or whoever is
 * here fills it from documents and cards. One at a time is deliberate. Four
 * half-filled departments teach Orbit less than one true one, and the order
 * puts the department with the most open cards first.
 *
 * A department Orbit only suspects (spotted in a document, never confirmed)
 * is shown as itself and has to be confirmed before it can be filled: a map
 * of a department that does not exist is worse than no map.
 */

const initials = (text: string): string => text.split(/[\s,]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

export const PhaseDepartments: React.FC<{
  clientId: string;
  departments: OrbitOverviewDossier[];
  people: OrbitRoomPerson[];
  rooms: OrbitRoomDepartment[];
  canPeople: boolean;
  /** Adding a department reshapes the org, which is the Owner's call, not
   *  every people-manager's. */
  canDepartments: boolean;
  canInvite: boolean;
  onBack: () => void;
  onOpen: (key: string) => void;
  onAddDepartment: () => void;
  onDecide: (d: OrbitOverviewDossier, accept: boolean) => void;
  onChanged: () => void;
}> = ({ clientId, departments, people, rooms, canPeople, canDepartments, canInvite, onBack, onOpen, onAddDepartment, onDecide, onChanged }) => {
  const [open, setOpen] = useState<string>(departments.length > 0 ? departments[0].key : '');
  const [busy, setBusy] = useState('');
  /* Dropping a document used to slide a drawer over the departments you were
     reading in order to decide what to drop. The buttons below open the file
     picker straight away instead; nothing is drawn for it. */
  const context = useRef<ContextPickerHandle>(null);

  const isMapped = (d: OrbitOverviewDossier): boolean => d.kind === 'department' && d.ask === 0 && d.known > 0;
  const mapped = departments.filter(isMapped).length;
  // One at a time: the first department that is not finished is the open one,
  // and the rest stay shut. Four half-filled departments teach Orbit less than
  // one true one.
  const openIndex = departments.findIndex((d) => !isMapped(d));
  const idOf = (slug: string): string => {
    const room = rooms.find((r) => r.slug === slug);
    if (!room) return '';
    return room.id;
  };

  const setHead = async (d: OrbitOverviewDossier, email: string) => {
    const id = idOf(d.key);
    if (!id) {
      toast.error('Orbit has no record of this department yet.');
      return;
    }
    setBusy(d.key);
    try {
      await aaiApi.updateDepartment(id, { lead_email: email });
      toast.success(`${shortName(email, people)} leads ${d.name}. Their cards route to them from now on.`);
      onChanged();
    } catch {
      toast.error('Could not set the head.');
    } finally {
      setBusy('');
    }
  };

  // The head is on the roster already; this is the email that lets them sign
  // in and answer. Nothing else about the department changes.
  const invite = async (d: OrbitOverviewDossier) => {
    if (!d.head_email) return;
    setBusy(d.key);
    try {
      await inviteCompanyMember(clientId, {
        email: d.head_email,
        name: d.head || undefined,
        role: 'DEPARTMENT_HEAD',
        department_id: idOf(d.key) || null,
        send_email: true,
      });
      toast.success(`Invite sent to ${d.head_email}. When they answer, this map completes.`);
      onChanged();
    } catch (err) {
      toast.error((err as Error).message || 'Could not send that invite.');
    } finally {
      setBusy('');
    }
  };

  return (
    <>
      <div className="det-head">
        <button type="button" className="back" onClick={onBack}>← Company</button>
        <h1>Department context</h1>
        <span className="pct"><b>{mapped}</b> of {departments.length} departments · one at a time</span>
      </div>
      <div className="dept-intro">
        Orbit maps one department at a time: <b>finish one, the next opens.</b> For each, bring the head in to make it true, or fill it
        yourself from documents. The order puts the department with the most still to learn first.
      </div>
      <ContextPicker ref={context} clientId={clientId} onDone={onChanged} />
      {departments.map((d, i) => {
        let state = 'locked';
        if (isMapped(d)) state = 'done';
        if (i === openIndex) state = 'now';
        const isOpen = state === 'now' && open === d.key;
        const headName = d.head || '';
        const first = headName ? headName.split(' ')[0] : '';
        const candidates = people.filter((p) => p.email);
        if (d.kind === 'proposed_department') {
          return (
            <div key={d.key} className="dept-card proposed">
              <div className="dept-head2">
                <span className="n">{i + 1}</span>
                <div>
                  <strong>{d.name}</strong>
                  <small>Spotted in {d.detected.replace(/^doc:|^url:/, '')}. Nobody has confirmed it exists.</small>
                </div>
                <em>To confirm</em>
              </div>
              <div className="dept-body open">
                <p>Orbit prepared {d.fields_total} cards for this department. They route to its head the moment it exists.</p>
                {canPeople && (
                  <div className="fill-opt fa-only">
                    <div className="fa">
                      <button type="button" className="pri" onClick={() => onDecide(d, true)}>Add {d.name}</button>
                      <button type="button" onClick={() => onDecide(d, false)}>Not a department</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }
        return (
          <div key={d.key} className={clsx('dept-card', state, isOpen && 'open')}>
            <div
              className="dept-head2"
              role="button"
              tabIndex={0}
              onClick={() => { if (state === 'now') setOpen(isOpen ? '' : d.key); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && state === 'now') setOpen(isOpen ? '' : d.key); }}
            >
              <span className="n">{state === 'done' ? '✓' : i + 1}</span>
              <div>
                <strong>{d.name}</strong>
                <small>{d.members || 0} people · {d.known} filled · {d.ask} to ask{headName ? ` · head: ${headName}` : ' · no head yet'}</small>
              </div>
              <em>{state === 'done' ? 'Mapped' : ''}{state === 'now' ? 'Open now' : ''}{state === 'locked' ? 'Locked' : ''}</em>
            </div>
            <div className="dept-body">
              <p>{d.ask} cards left on this department. Make it true one of two ways:</p>
              <div className="fill-two">
                <div className="fill-opt hl">
                  <div className="ft2"><b>{initials(headName || d.name)}</b>{headName ? `Bring ${first} in` : 'Name a head'}</div>
                  <small>
                    {headName
                      ? `${headName} logs in and Orbit asks only the questions its draft could not answer, in their words.`
                      : 'Orbit holds this map until somebody owns it. Pick the person who runs this department.'}
                  </small>
                  <div className="fa">
                    {canPeople && (
                      <select
                        className="role-sel"
                        value={d.head_email || ''}
                        disabled={busy === d.key}
                        onChange={(e) => setHead(d, e.target.value)}
                      >
                        <option value="">Pick the head</option>
                        {candidates.map((p) => <option key={p.email} value={p.email}>{p.name || p.email}</option>)}
                      </select>
                    )}
                    {canInvite && d.head_email && <button type="button" className="pri" disabled={busy === d.key} onClick={() => invite(d)}>Invite {first}</button>}
                  </div>
                </div>
                <div className="fill-opt">
                  <div className="ft2"><b>YOU</b>Fill it yourself</div>
                  <small>Drop the department documents, or answer the cards. Orbit reads and files everything with its source.</small>
                  <div className="fa">
                    <button type="button" onClick={() => context.current?.pick()}>Upload docs</button>
                    <button type="button" onClick={() => onOpen(d.key)}>Answer the cards</button>
                  </div>
                </div>
              </div>
              <button type="button" className="dept-go" onClick={() => onOpen(d.key)}>
                {headName ? `Deep dive with ${first}, the department session` : 'Open the department session'}
              </button>
            </div>
          </div>
        );
      })}
      {departments.length === 0 && (
        <div className="empty">
          <strong>No departments yet.</strong>
          Orbit opens one for every department it finds in the website or a document. Add one yourself to start.
        </div>
      )}
      {canDepartments && (
        <div className="aux-row">
          <span>Missing one?</span>
          <div className="aux-acts">
            <button type="button" onClick={onAddDepartment}>Add a department</button>
            <button type="button" onClick={() => context.current?.pick()}>Drop an org chart</button>
          </div>
        </div>
      )}
    </>
  );
};

export default PhaseDepartments;
